/**
 * Google Analytics 4 Integration for Local Community Platform
 *
 * This module replaces Datadog RUM with Google Analytics 4 for web monitoring,
 * providing user session tracking, Core Web Vitals monitoring, and custom events.
 *
 * All events automatically include:
 * - app_version: Git version/tag for tracking releases
 * - environment: deployment environment (production, staging, dev)
 * - correlation_id: For tracing events across frontend/backend
 */

import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";
import { getBuildInfo } from "../build-info";

// Web Vitals metric interface (based on web-vitals v5 callback format)
interface Metric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  delta: number;
  id: string;
}

// Google Analytics configuration
export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";

// Session correlation ID for tracking user sessions across frontend and backend
let sessionCorrelationId: string | null = null;

/**
 * Generate a correlation ID for tracking events across services
 * Format matches OpenTelemetry trace ID (32-char hex)
 */
function generateCorrelationId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

/**
 * Get or create the session correlation ID
 */
function getCorrelationId(): string {
  if (!sessionCorrelationId) {
    // Try to get from sessionStorage first (persists across page loads)
    if (typeof window !== "undefined" && window.sessionStorage) {
      sessionCorrelationId = sessionStorage.getItem("correlation_id");
    }

    // Generate new one if not found
    if (!sessionCorrelationId) {
      sessionCorrelationId = generateCorrelationId();
      if (typeof window !== "undefined" && window.sessionStorage) {
        sessionStorage.setItem("correlation_id", sessionCorrelationId);
      }
    }
  }

  return sessionCorrelationId;
}

/**
 * Get default parameters that are added to ALL events
 * These parameters enable:
 * - Release tracking (app_version)
 * - Environment filtering (environment)
 * - Cross-service tracing (correlation_id)
 */
function getDefaultEventParameters(): Record<string, string> {
  const buildInfo = getBuildInfo();
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "unknown";

  return {
    app_version: buildInfo.version,
    environment: env,
    correlation_id: getCorrelationId(),
  };
}

// Type definitions for enhanced ecommerce events
interface GAEvent {
  action: string;
  category:
    | "analytics"
    | "auth"
    | "business_metric"
    | "business"
    | "conversion"
    | "engagement_time"
    | "engagement"
    | "entity_interaction"
    | "error"
    | "event"
    | "form"
    | "onboarding"
    | "scroll_depth"
    | "search"
    | "security"
    | "technical"
    | "user_journey";
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

interface GAEcommerceEvent {
  currency?: string;
  value?: number;
  items?: Array<{
    item_id: string;
    item_name: string;
    category?: string;
    quantity?: number;
    price?: number;
  }>;
}

interface GAUserProperties {
  user_id?: string;
  user_type?: "registered" | "guest";
  preferred_language?: string;
  user_engagement_level?: "high" | "medium" | "low";
}

// Extend the Window interface for gtag
declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js" | "set",
      targetId: string | Date | GAUserProperties,
      config?: any,
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Initialize Google Analytics 4
 * Sets up global tracking with version and environment context
 */
export const initGA4 = (): void => {
  if (!GA_MEASUREMENT_ID) {
    console.warn("GA4: Measurement ID not provided");
    return;
  }

  const buildInfo = getBuildInfo();
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "unknown";

  // Load gtag script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    // Privacy settings
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,

    // Performance and debugging
    send_page_view: true,
    debug_mode: process.env.NODE_ENV === "development",

    // Global custom dimensions (appear on all events)
    app_version: buildInfo.version,
    environment: env,
    correlation_id: getCorrelationId(),

    // Enhanced ecommerce
    custom_map: {
      custom_parameter_1: "entity_type",
      custom_parameter_2: "entity_id",
      custom_parameter_3: "user_role",
      custom_parameter_4: "page_type",
    },
  });

  // Initialize Core Web Vitals tracking
  initWebVitals();
};

/**
 * Track a page view
 */
export const trackPageView = (url: string, title?: string): void => {
  if (!window.gtag) return;

  window.gtag("config", GA_MEASUREMENT_ID, {
    page_location: url,
    page_title: title,
  });
};

/**
 * Track a custom event
 * Automatically includes version, environment, and correlation_id for tracking
 */
export const trackEvent = (event: GAEvent): void => {
  if (!window.gtag) return;

  const defaultParams = getDefaultEventParameters();

  window.gtag("event", event.action, {
    event_category: event.category,
    event_label: event.label,
    value: event.value,
    // Default params first (can be overridden by custom_parameters if needed)
    ...defaultParams,
    ...event.custom_parameters,
  });
};

/**
 * Track user properties
 */
export const setUserProperties = (properties: GAUserProperties): void => {
  if (!window.gtag) return;

  window.gtag("set", properties);
};

/**
 * Track entity interactions (replaces custom Datadog events)
 */
export const trackEntityInteraction = (
  entityType: "event" | "product" | "service" | "organization" | "review",
  entityId: string,
  action: "view" | "save" | "share" | "register" | "contact" | "review",
  additionalData?: Record<string, any>,
): void => {
  trackEvent({
    action: `${entityType}_${action}`,
    category: "entity_interaction",
    label: entityId,
    custom_parameters: {
      entity_type: entityType,
      entity_id: entityId,
      ...additionalData,
    },
  });
};

/**
 * Track business conversion events
 */
export const trackConversion = (
  type: "registration" | "subscription" | "purchase" | "contact",
  value?: number,
  currency: string = "USD",
  additionalData?: Record<string, any>,
): void => {
  trackEvent({
    action: "conversion",
    category: "business",
    label: type,
    value,
    custom_parameters: {
      conversion_type: type,
      currency,
      ...additionalData,
    },
  });
};

/**
 * Track search interactions
 */
export const trackSearch = (
  query: string,
  category?: string,
  resultsCount?: number,
): void => {
  trackEvent({
    action: "search",
    category: "engagement",
    label: query,
    custom_parameters: {
      search_term: query,
      search_category: category,
      results_count: resultsCount,
    },
  });
};

/**
 * Track user onboarding progress
 */
export const trackOnboardingStep = (
  step: string,
  progress: number,
  userType: "organization" | "consumer",
  method: string,
  status: string,
): void => {
  trackEvent({
    action: "onboarding_step",
    category: "user_journey",
    label: step,
    value: progress,
    custom_parameters: {
      onboarding_step: step,
      progress_percentage: progress,
      user_type: userType,
      method,
      status,
    },
  });
};

/**
 * Track errors (replaces Datadog RUM error tracking)
 */
export const trackError = (
  error: Error,
  context?: string,
  severity: "low" | "medium" | "high" = "medium",
): void => {
  trackEvent({
    action: "error",
    category: "technical",
    label: error.message,
    custom_parameters: {
      error_message: error.message,
      error_stack: error.stack?.substring(0, 500), // Truncate for GA4 limits
      error_context: context,
      error_severity: severity,
      page_url: window.location.href,
    },
  });
};

/**
 * Track form interactions
 */
export const trackFormInteraction = (
  formName: string,
  action: "start" | "complete" | "abandon",
  step?: string,
): void => {
  trackEvent({
    action: `form_${action}`,
    category: "engagement",
    label: formName,
    custom_parameters: {
      form_name: formName,
      form_step: step,
    },
  });
};

/**
 * Core Web Vitals tracking
 */
function initWebVitals(): void {
  function sendToGA4(metric: Metric): void {
    if (!window.gtag) return;

    window.gtag("event", metric.name, {
      event_category: "web_vitals",
      event_label: metric.id,
      value: Math.round(metric.value),
      custom_parameters: {
        metric_name: metric.name,
        metric_value: metric.value,
        metric_rating: metric.rating,
        metric_delta: metric.delta,
      },
    });
  }

  // Track all Core Web Vitals
  onCLS(sendToGA4);
  onFCP(sendToGA4);
  onINP(sendToGA4);
  onLCP(sendToGA4);
  onTTFB(sendToGA4);
}

/**
 * Enhanced ecommerce tracking
 */
export const trackEcommerce = (
  action: "view_item" | "add_to_cart" | "begin_checkout" | "purchase",
  data: GAEcommerceEvent,
): void => {
  if (!window.gtag) return;

  window.gtag("event", action, {
    currency: data.currency || "USD",
    value: data.value,
    items: data.items,
  });
};

/**
 * Track custom business metrics
 */
export const trackBusinessMetric = (
  metric: string,
  value: number,
  unit?: string,
  context?: Record<string, any>,
): void => {
  trackEvent({
    action: "business_metric",
    category: "analytics",
    label: metric,
    value,
    custom_parameters: {
      metric_name: metric,
      metric_unit: unit,
      ...context,
    },
  });
};

/**
 * Track user engagement time
 */
export const trackEngagementTime = (page: string, timeSpent: number): void => {
  trackEvent({
    action: "engagement_time",
    category: "engagement",
    label: page,
    value: Math.round(timeSpent / 1000), // Convert to seconds
    custom_parameters: {
      page_name: page,
      time_spent_ms: timeSpent,
    },
  });
};

/**
 * Privacy-compliant user identification
 */
export const identifyUser = (
  userId: string,
  userProperties?: GAUserProperties,
): void => {
  if (!window.gtag) return;

  // Set user ID for cross-session tracking
  window.gtag("config", GA_MEASUREMENT_ID, {
    user_id: userId,
  });

  // Set additional user properties
  if (userProperties) {
    setUserProperties({
      user_id: userId,
      ...userProperties,
    });
  }
};

/**
 * Clear user identification (for logout)
 */
export const clearUserIdentification = (): void => {
  if (!window.gtag) return;

  window.gtag("config", GA_MEASUREMENT_ID, {
    user_id: undefined,
  });
};

/**
 * Track scroll depth
 */
export const setupScrollTracking = (): void => {
  let maxScroll = 0;
  const thresholds = [25, 50, 75, 90, 100];
  const trackedThresholds = new Set<number>();

  const trackScrollDepth = (): void => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.body.scrollHeight - window.innerHeight)) *
        100,
    );

    if (scrollPercent > maxScroll) {
      maxScroll = scrollPercent;

      thresholds.forEach((threshold) => {
        if (scrollPercent >= threshold && !trackedThresholds.has(threshold)) {
          trackedThresholds.add(threshold);
          trackEvent({
            action: "scroll_depth",
            category: "engagement",
            label: `${threshold}%`,
            value: threshold,
            custom_parameters: {
              scroll_percentage: threshold,
              page_url: window.location.href,
            },
          });
        }
      });
    }
  };

  // Throttled scroll tracking
  let scrollTimeout: NodeJS.Timeout;
  window.addEventListener("scroll", () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(trackScrollDepth, 100);
  });
};

/**
 * Export correlation ID for use in API calls and logging
 * This enables tracing events across frontend and backend
 *
 * Usage in API calls:
 * ```typescript
 * const response = await fetch('/api/endpoint', {
 *   headers: {
 *     'X-Correlation-ID': getSessionCorrelationId()
 *   }
 * });
 * ```
 *
 * Usage in logging:
 * ```typescript
 * logger.error('Request failed', correlationIDField(getSessionCorrelationId()));
 * ```
 */
export const getSessionCorrelationId = getCorrelationId;

/**
 * Reset the correlation ID (e.g., on logout to start fresh session)
 */
export const resetCorrelationId = (): void => {
  sessionCorrelationId = null;
  if (typeof window !== "undefined" && window.sessionStorage) {
    sessionStorage.removeItem("correlation_id");
  }
};

/**
 * Export all tracking functions for easy use
 */
export const analytics = {
  init: initGA4,
  pageView: trackPageView,
  event: trackEvent,
  userProperties: setUserProperties,
  entityInteraction: trackEntityInteraction,
  conversion: trackConversion,
  search: trackSearch,
  onboardingStep: trackOnboardingStep,
  error: trackError,
  formInteraction: trackFormInteraction,
  ecommerce: trackEcommerce,
  businessMetric: trackBusinessMetric,
  engagementTime: trackEngagementTime,
  identifyUser,
  clearUserIdentification,
  setupScrollTracking,
  getCorrelationId: getSessionCorrelationId,
  resetCorrelationId,
};
